<template>
  <div class="mit-directions-page white">
    <BaseMitLoading class="mit-progress" :show="loading" :overlayFullscreen="false"/>
    <div v-if="!navWizard.inProgress && !loading" class="wizard-overlay d-flex align-center justify-center pa-3">
        <v-card max-width="500" width="100%" class="pa-4 pa-sm-6 rounded-xl elevation-6">
            <v-card-title class="pa-0 mb-4 text-h5 font-weight-bold">
                <v-icon left color="primary" x-large>mdi-directions</v-icon> 
                {{ isQrMode ? 'You Are Here' : 'Get Directions' }}
                <v-spacer></v-spacer>
                <!-- Hidden until a destination is chosen -->
                <v-tooltip v-if="user && navWizard.destObj" bottom max-width="300">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn icon color="grey darken-2" :loading="downloadingQr" @click="downloadDirectionsQr()" v-bind="attrs" v-on="on">
                            <!-- Simplified QR (three corner squares) with a download arrow in the fourth corner -->
                            <svg class="qr-download-icon" viewBox="0 0 24 24" aria-hidden="true">
                                <g fill="none" stroke="currentColor" stroke-width="1.5">
                                    <rect x="2.75" y="2.75" width="6.5" height="6.5" rx="1"/>
                                    <rect x="14.75" y="2.75" width="6.5" height="6.5" rx="1"/>
                                    <rect x="2.75" y="14.75" width="6.5" height="6.5" rx="1"/>
                                </g>
                                <g fill="currentColor">
                                    <rect x="5" y="5" width="2" height="2"/>
                                    <rect x="17" y="5" width="2" height="2"/>
                                    <rect x="5" y="17" width="2" height="2"/>
                                    <rect x="11.25" y="3" width="1.5" height="1.5"/>
                                    <rect x="11.25" y="7" width="1.5" height="1.5"/>
                                    <rect x="3" y="11.25" width="1.5" height="1.5"/>
                                    <rect x="7" y="11.25" width="1.5" height="1.5"/>
                                    <rect x="11.25" y="11.25" width="1.5" height="1.5"/>
                                </g>
                                <g fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" opacity="0.75">
                                    <path d="M18 13.5v6"/>
                                    <path d="M15.5 17l2.5 2.5 2.5-2.5"/>
                                    <path d="M15 21.75h6"/>
                                </g>
                            </svg>
                        </v-btn>
                    </template>
                    <div>
                        <div class="font-weight-bold mb-1">Download a shareable QR code for this direction</div>
                        <div>Leave the starting point empty if you want people to fill it in, or set it if this QR code will be placed at a known starting point.</div>
                    </div>
                </v-tooltip>
            </v-card-title>
            
            <div v-if="!isQrMode">
                <p class="text-subtitle-1 mb-2 font-weight-medium">Where are you starting from?</p>
                <v-autocomplete outlined v-model="navWizard.startObj" :items="startOptions" :item-text="getSpaceDisplayText" :filter="customSearchFilter" return-object label="Search" clearable class="mb-2">
                    <template v-slot:item="{ item }">
                        <!-- 1. GROUPS -->
                        <template v-if="item.isNeighborhood">
                            <v-list-item-avatar>
                                <v-icon color="primary">mdi-account-group</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-title>
                                    <span>{{ item.name }}{{ (item.name || '').toLowerCase().includes('group') ? '' : ' Group' }}</span>
                                    <span class="grey--text text--darken-1 text-caption ml-1">
                                        (Main Space: {{ item.mitId }})<span v-if="item.piList && item.piList.length"> - PI {{ Array.isArray(item.piList) ? item.piList.join(', ') : item.piList }}</span>
                                    </span>
                                </v-list-item-title>
                            </v-list-item-content>
                        </template>

                        <!-- 2. MAP OBJECTS (e.g., Printers, Phone booths) -->
                        <template v-else-if="item.spaceType === 'object'">
                            <v-list-item-avatar>
                                <v-icon>mdi-cube-outline</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <div v-if="item.name && item.name.length > 0">
                                    <v-list-item-title v-html="item.name"></v-list-item-title>
                                    <v-list-item-title class="text-caption grey--text text--darken-1" v-html="item.mitIdShort"></v-list-item-title>
                                    <v-list-item-subtitle class="primary--text caption" v-html="item.useType"></v-list-item-subtitle>
                                </div>
                                <div v-else>
                                    <v-list-item-title v-html="item.mitIdShort"></v-list-item-title>
                                    <v-list-item-subtitle class="primary--text caption" v-html="item.useType"></v-list-item-subtitle>
                                </div>
                            </v-list-item-content>
                        </template>
                        
                        <!-- 3. FLOORS -->
                        <template v-else-if="typeof item.spaceType === 'floor'">
                        <v-list-item-content v-text="'Floor '+item.level"></v-list-item-content>
                        </template>

                        <!-- 4. STANDARD SPACES / ROOMS -->
                        <template v-else>
                            <v-list-item-avatar>
                            <v-icon>mdi-office-building-outline</v-icon>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <div v-if="item.name && item.name.length > 0">
                                    <v-list-item-title v-html="item.name"></v-list-item-title>
                                    <v-list-item-title class="text-caption grey--text text--darken-1" v-html="item.mitIdShort || item.mitId"></v-list-item-title>
                                    <v-list-item-subtitle class="primary--text caption" v-html="item.useType || item.spaceType"></v-list-item-subtitle>
                                </div>
                                <div v-else>
                                    <v-list-item-title v-html="item.mitIdShort || item.mitId"></v-list-item-title>
                                    <v-list-item-subtitle class="primary--text caption" v-html="item.useType || item.spaceType"></v-list-item-subtitle>
                                </div>
                            </v-list-item-content>
                        </template>
                    </template>
                </v-autocomplete>
            </div>
            <div v-else class="mb-4">
                <p class="text-subtitle-1 mb-2 font-weight-medium">Where are you starting from?</p>
                <v-sheet 
                    color="grey lighten-4" 
                    class="d-flex align-center justify-space-between pa-3 rounded"
                    outlined
                >
                    <div class="d-flex align-center overflow-hidden">
                        <v-avatar color="primary" size="36" class="mr-3 rounded">
                            <v-icon color="white" small>mdi-qrcode-scan</v-icon>
                        </v-avatar>
                        <div class="text-truncate">
                            <div class="font-weight-bold text-subtitle-2 primary--text text-truncate">
                                {{ navWizard.startObj ? (navWizard.startObj.name || navWizard.startObj.mitIdShort || navWizard.startObj.mitId) : 'Locating...' }}
                            </div>
                            <div class="text-caption grey--text text--darken-1" style="line-height: 1.2;">Locked via QR Scan</div>
                        </div>
                    </div>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn icon color="grey darken-2" @click="clearQrStart" v-bind="attrs" v-on="on">
                                <v-icon>mdi-close-circle</v-icon>
                            </v-btn>
                        </template>
                        <span>Change starting point</span>
                    </v-tooltip>
                </v-sheet>
            </div>
            
            <p class="text-subtitle-1 mb-2 font-weight-medium">Where do you want to go?</p>
            <v-autocomplete outlined v-model="navWizard.destObj" :items="destOptions" :item-text="getSpaceDisplayText" :filter="customSearchFilter" return-object label="Search" clearable class="mb-2">
                <!-- <template v-slot:append-outer>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon v-bind="attrs" v-on="on" class="mr-2" color="grey darken-2">mdi-help-circle-outline</v-icon>
                        </template>
                        <span>Search for things like Rooms, bathrooms, printers, phone booths, Lab PIs and more...</span>
                    </v-tooltip>
                </template> -->
                <template v-slot:item="{ item }">
                    <!-- 1. GROUPS -->
                    <template v-if="item.isNeighborhood">
                        <v-list-item-avatar>
                            <v-icon color="primary">mdi-account-group</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                            <v-list-item-title>
                                <span>{{ item.name }}{{ (item.name || '').toLowerCase().includes('group') ? '' : ' Group' }}</span>
                                <span class="grey--text text--darken-1 text-caption ml-1">
                                    (Main Space: {{ item.mitId }})<span v-if="item.piList && item.piList.length"> - PI {{ Array.isArray(item.piList) ? item.piList.join(', ') : item.piList }}</span>
                                </span>
                            </v-list-item-title>
                        </v-list-item-content>
                    </template>

                    <!-- 2. MAP OBJECTS (e.g., Printers, Phone booths) -->
                    <template v-else-if="item.spaceType === 'object'">
                        <v-list-item-avatar>
                            <v-icon>mdi-cube-outline</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                            <div v-if="item.name && item.name.length > 0">
                                <v-list-item-title v-html="item.name"></v-list-item-title>
                                <v-list-item-title class="text-caption grey--text text--darken-1" v-html="item.mitIdShort"></v-list-item-title>
                                <v-list-item-subtitle class="primary--text caption" v-html="item.useType"></v-list-item-subtitle>
                            </div>
                            <div v-else>
                                <v-list-item-title v-html="item.mitIdShort"></v-list-item-title>
                                <v-list-item-subtitle class="primary--text caption" v-html="item.useType"></v-list-item-subtitle>
                            </div>
                        </v-list-item-content>
                    </template>
                    
                    <!-- 3. FLOORS -->
                    <template v-else-if="typeof item.spaceType === 'floor'">
                    <v-list-item-content v-text="'Floor '+item.level"></v-list-item-content>
                    </template>

                    <!-- 4. STANDARD SPACES / ROOMS -->
                    <template v-else>
                        <v-list-item-avatar>
                        <v-icon>mdi-office-building-outline</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                            <div v-if="item.name && item.name.length > 0">
                                <v-list-item-title v-html="item.name"></v-list-item-title>
                                <v-list-item-title class="text-caption grey--text text--darken-1" v-html="item.mitIdShort || item.mitId"></v-list-item-title>
                                <v-list-item-subtitle class="primary--text caption" v-html="item.useType || item.spaceType"></v-list-item-subtitle>
                            </div>
                            <div v-else>
                                <v-list-item-title v-html="item.mitIdShort || item.mitId"></v-list-item-title>
                                <v-list-item-subtitle class="primary--text caption" v-html="item.useType || item.spaceType"></v-list-item-subtitle>
                            </div>
                        </v-list-item-content>
                    </template>
                </template>
            </v-autocomplete>

            <p class="text-subtitle-2 mb-2 grey--text text--darken-1 font-weight-medium">Routing Options</p>
            <div class="mb-4 d-flex flex-wrap" style="gap: 8px;">
                <v-btn small rounded :color="navWizard.routingOption === 'fastest' ? 'primary' : 'grey lighten-3'" :class="navWizard.routingOption === 'fastest' ? '' : 'black--text'" elevation="0" @click="setRoutingOption('fastest')">
                    <v-icon left small>mdi-run</v-icon> Fastest
                </v-btn>
                <v-btn small rounded :color="navWizard.routingOption === 'avoid_stairs' ? 'primary' : 'grey lighten-3'" :class="navWizard.routingOption === 'avoid_stairs' ? '' : 'black--text'" elevation="0" @click="setRoutingOption('avoid_stairs')">
                    <v-icon left small>mdi-stairs</v-icon> Avoid Stairs
                </v-btn>
                <v-btn small rounded :color="navWizard.routingOption === 'avoid_elevators' ? 'primary' : 'grey lighten-3'" :class="navWizard.routingOption === 'avoid_elevators' ? '' : 'black--text'" elevation="0" @click="setRoutingOption('avoid_elevators')">
                    <v-icon left small>mdi-elevator-down</v-icon> Avoid Elevators
                </v-btn>
            </div>
            
            <div class="error--text font-weight-bold mb-4" v-if="navWizard.errorMsg">
                <v-icon color="error" left>mdi-alert-circle</v-icon> {{navWizard.errorMsg}}
            </div>

            <v-card-actions class="pa-0 mt-4">
                <v-spacer></v-spacer>
                <v-btn color="primary" large block @click="startNavigationMode()" :disabled="!navWizard.startObj || !navWizard.destObj">
                    Get Directions
                </v-btn>
            </v-card-actions>
        </v-card>
    </div>

    <v-card flat class="map-container-card white" :style="navWizard.inProgress ? 'visibility: visible; opacity: 1;' : 'visibility: hidden; opacity: 0; pointer-events: none;'">
        
        <div class="toolbar-drawing-right">
            <v-card elevation="4" class="mt-2" color="blue lighten-5" max-width="100%" width="400"> 
                <v-card-title class="subtitle-1 font-weight-bold">
                    Directions
                    <v-spacer></v-spacer>
                    <v-btn icon small @click="cancelNavigation()"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <div class="d-flex justify-space-between align-start">
                    <div class="flex-grow-1 pr-2 overflow-hidden">
                        <div class="text-body-2 mb-1 text-truncate">
                            <b>From:</b> {{navWizard.startObj ? (navWizard.startObj.name || navWizard.startObj.mitIdShort || navWizard.startObj.mitId) : ''}}
                        </div>
                        <div class="text-body-2 mb-2 text-truncate">
                            <b>To:</b> {{navWizard.destObj ? (navWizard.destObj.name || navWizard.destObj.mitIdShort || navWizard.destObj.mitId) : ''}}
                        </div>
                    </div>
                    
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                icon
                                small
                                class="mt-1 flex-shrink-0"
                                :color="showStructures ? 'primary' : 'grey darken-1'"
                                :loading="loadingStructures"
                                @click="toggleStructures"
                                v-bind="attrs"
                                v-on="on"
                            >
                                <v-icon>mdi-layers-outline</v-icon>
                            </v-btn>
                        </template>
                        <span>Toggle structural details</span>
                    </v-tooltip>
                </div>
                    <v-divider class="my-2"></v-divider>
                    
                    <div v-if="currentInstruction">
                        <v-alert dense type="info" color="blue darken-2" dark class="mb-3 mt-2 elevation-2">
                            <div class="text-subtitle-2 mb-1">Floor {{currentInstruction.floor}}</div>
                            <div>{{stepInstructions}}</div>
                        </v-alert>
                        
                        <div class="d-flex" style="gap: 8px;">
                            <v-btn v-if="navWizard.currentRouteIndex > 0" outlined color="primary" @click="prevNavStep()">
                                <v-icon left>mdi-arrow-left</v-icon> Back
                            </v-btn>
                            <v-btn color="primary" class="flex-grow-1" @click="nextNavStep()">
                                {{nextButtonText}} <v-icon right v-if="navWizard.currentRouteIndex < navWizard.route.length - 1">mdi-arrow-right</v-icon>
                            </v-btn>
                        </div>
                    </div>
                </v-card-text>
            </v-card>
        </div>

        <div class="scrollable">
            <svg id="mit-svg" style=""></svg>
        </div>
        
        <div class="zoom-controls">
            <v-btn dark class="zoom-in-btn mb-2" fab small color="grey darken-2"><v-icon>mdi-plus</v-icon></v-btn>
            <v-btn dark class="zoom-out-btn mb-2" fab small color="grey darken-2"><v-icon>mdi-minus</v-icon></v-btn>
            <v-btn dark class="zoom-reset-btn" fab small color="grey darken-2"><v-icon>mdi-restart</v-icon></v-btn>
        </div>
    </v-card>

  </div>
</template>

<script>
import AnalyticsTracker from '../services/MitAnalyticsTracker.js';
import BaseMitLoading from '../../base/components/BaseMitLoading.vue';
import MitMapService from '../services/MitMapService.js';
import MitSpacesService from '../services/MitSpacesService.js';
import MitDirectionsDisplayService from '../services/MitDirectionsDisplayService.js';
import MitNavigationEngine from '../services/MitNavigationEngine.js';
import MitSpacesSettingsService from '../services/MitSpacesSettingsService.js';
import MitDirectionsQrService from '../services/MitDirectionsQrService.js';
import BaseMitUserService from '../../base/services/BaseMitUserService.js';

export default {
  name: 'MitDirectionsPage',
  components: { BaseMitLoading },
  data() {
    return {
        loading: true,
        buildingMitId: '32',
        spacesList: [],
        neighborhoods: [],
        spacesModel: {},
        doorsModel: null,
        windowsModel: null,
        structuresModel: null,
        objectsModel: null,
        navFeaturesModel: [],
        info:{ levels:[], spaceTypes:[], spaceUses:[], orgs:[], floorDict:{} },
        floors:[],
        selectedFloor: 0,
        showStructures: true,
        loadingStructures: false,
        structuresLoaded: false,
        navEngine: new MitNavigationEngine(),
        navWizard: { 
            inProgress: false, 
            startObj: null, 
            destObj: null, 
            routingOption: 'fastest',
            route: [], 
            currentRouteIndex: 0, 
            errorMsg: '' 
        },

        size:{ w:1000, h:800 },
        mapService: null,
        directionsDisplayService: null,
        palletObjects: [],
        settingsService: new MitSpacesSettingsService(),
        
        isQrMode: false,
        qrParamId: null,

        user: null,
        downloadingQr: false,
        fromCustomQr: false
    }
  },
  watch: {
      'navWizard.startObj'(newVal) { 
          this.updateUrl(); 
          if (newVal) this.trackSearchSelection(newVal, 'start_location');
      },
      'navWizard.destObj'(newVal) { 
          this.updateUrl(); 
          if (newVal) this.trackSearchSelection(newVal, 'destination_location');
      },
      'navWizard.routingOption'() {            
          this.updateUrl();            
          if (this.navWizard.inProgress && this.navWizard.startObj && this.navWizard.destObj) {               
              this.startNavigationMode();           
          }       
      }
  },
  computed: {
    startOptions() {
        let opts = [...this.spacesList];
        if (this.neighborhoods) {
            let groupOpts = this.neighborhoods.filter(g => g.headSpaceId && g.spaceIds && g.spaceIds.length > 0).map(g => {
                let headSpace = this.spacesList.find(s => s.mitId === g.headSpaceId);
                return {
                    isNeighborhood: true,
                    id: g.id, // <-- Add this group ID
                    name: g.name,
                    mitId: g.headSpaceId, // Uses head space for routing
                    piList: g.piList,
                    useType: 'Group', // Ensure this was changed from 'Neighborhood'
                    level: headSpace ? headSpace.level : null,
                    centroid: headSpace ? headSpace.centroid : null,
                    mitBuildingId: headSpace ? headSpace.mitBuildingId : this.buildingMitId
                };
            });
            opts = [...groupOpts, ...opts];
        }
        return opts;
    },
    destOptions() { return this.startOptions; },
    currentInstruction() {
        if (!this.navWizard.route || this.navWizard.route.length === 0) return null;
        return this.navWizard.route[this.navWizard.currentRouteIndex];
    },
    stepInstructions() {
        if (!this.currentInstruction) return '';
        let isLastStep = this.navWizard.currentRouteIndex === this.navWizard.route.length - 1;
        
        if (isLastStep) {
            let destName = this.navWizard.destObj ? (this.navWizard.destObj.name || this.navWizard.destObj.mitIdShort || this.navWizard.destObj.mitId) : 'Destination';
            return `Walk to your destination: ${destName}.`;
        } else {
            let nextFloor = this.navWizard.route[this.navWizard.currentRouteIndex + 1].floor;
            let features = this.currentInstruction.routeFeatures;
            let transitionName = "stairs or elevator";
            
            // Check the last feature of the current floor's route (the transition point)
            if (features && features.length > 0) {
                let lastFeature = features[features.length - 1];
                if (lastFeature && lastFeature.properties) {
                    if (lastFeature.properties.name) {
                        transitionName = lastFeature.properties.name; // Use the specific name
                    } else if (lastFeature.properties.levelChangeType) {
                        transitionName = lastFeature.properties.levelChangeType.toLowerCase(); // Use 'stairs' or 'elevator'
                    } else if (lastFeature.properties.useType) {
                        // Fallback to the general useType if levelChangeType isn't set yet
                        let type = lastFeature.properties.useType.toLowerCase();
                        if (type === 'stairs' || type === 'elevator') {
                            transitionName = type;
                        }
                    }
                }
            }
            
            return `Proceed to the ${transitionName} and go to Floor ${nextFloor}.`;
        }
    },
    nextButtonText() {
        if (!this.currentInstruction) return '';
        let isLastStep = this.navWizard.currentRouteIndex === this.navWizard.route.length - 1;
        
        if (isLastStep) return 'Finish Navigation';
        
        let nextFloor = this.navWizard.route[this.navWizard.currentRouteIndex + 1].floor;
        return `Next: Go to Floor ${nextFloor}`;
    }
  },
  async mounted() {
    // 1. Await the session initialization to ensure the backend creates the session
    // 1. Await the session initialization to ensure the backend creates the session
    await AnalyticsTracker.initSession();
    // Only logged in CSAIL users can generate directions QR codes
    BaseMitUserService.getUser()
        .then((user) => { this.user = user; })
        .catch(() => { this.user = null; });
    // Custom (user generated) QR scan: remember it, then drop src so a refresh doesn't count it again
    if (this.$route && this.$route.query && this.$route.query.src === 'custom') {
        this.fromCustomQr = true;
        let cleanQuery = Object.assign({}, this.$route.query);
        delete cleanQuery.src;
        this.$router.replace({ query: cleanQuery }).catch(err => {
            if (err.name !== 'NavigationDuplicated') throw err;
        });
    }
    let queryBuilding = this.$route && this.$route.query && this.$route.query.b;
    if(queryBuilding) this.buildingMitId = queryBuilding;
    
    // Check for QR parameter and extract building ID if present
    let qrQuery = this.$route && this.$route.query && this.$route.query.qr;
    if(qrQuery) {
        this.isQrMode = true;
        this.qrParamId = qrQuery;
        
        if (this.qrParamId.includes('QR_POINT')) {
            this.buildingMitId = this.qrParamId.split('_')[0];
        } else if (this.qrParamId.includes('-')) {
            this.buildingMitId = this.qrParamId.split('-')[0];
        }

    }

    this.getObjectDefinitions().finally(() => {
        this.mapService = new MitMapService("#mit-svg", this.palletObjects, null, null, null);
        this.directionsDisplayService = new MitDirectionsDisplayService(this.mapService);
                   
        window.DEBUG_NAV_ENGINE = this.navEngine; 
        this.init();
    });
  },
  methods : {
    // Simple user directions QR (separate from manager QR_POINT signage)
    async downloadDirectionsQr() {
        const start = this.navWizard.startObj;
        const dest = this.navWizard.destObj;
        if (!dest) return;

        const params = new URLSearchParams();
        params.set('b', this.buildingMitId);
        if (this.isQrMode && this.qrParamId) {
            params.set('qr', this.qrParamId);
        } else if (start) {
            params.set('start', start.mitId);
        }
        params.set('end', dest.mitId);
        params.set('opt', this.navWizard.routingOption);
        params.set('src', 'custom');
        const url = `${window.location.origin}/directions?${params.toString()}`;

        const displayName = (o) => o.name || o.mitIdShort || o.mitId;
        const fileName = `tig-directions-${String(dest.mitIdShort || dest.mitId).replace(/[^\w-]+/g, '_')}.png`;

        this.downloadingQr = true;
        try {
            await MitDirectionsQrService.download(url, {
                from: start ? displayName(start) : null,
                to: displayName(dest)
            }, fileName);
            // csail_id of the generator is added server side for 'custom_qr' events
            AnalyticsTracker.track('custom_qr', 'generated', this.customQrMetricData());
        } catch (err) {
            console.error('Failed to create directions QR code', err);
            this.navWizard.errorMsg = 'Could not create the QR code.';
        } finally {
            this.downloadingQr = false;
        }
    },
    customQrMetricData() {
        const start = this.navWizard.startObj;
        const dest = this.navWizard.destObj;
        return {
            building_id: this.buildingMitId,
            start_id: start ? start.mitId : null,
            start_name: start ? (start.name || start.mitIdShort || start.mitId) : null,
            dest_id: dest ? dest.mitId : null,
            dest_name: dest ? (dest.name || dest.mitIdShort || dest.mitId) : null,
            routing_option: this.navWizard.routingOption
        };
    },
    clearQrStart() {
        this.isQrMode = false;
        this.qrParamId = null;
        this.navWizard.startObj = null;
        this.updateUrl();
    },
    // Analytics Metric 1: Direction Routing Options Clicked
      setRoutingOption(option) {
          this.navWizard.routingOption = option;
          AnalyticsTracker.track('directions', 'click_routing_option', { option: option });
      },

      // Analytics Metrics 3, 4, 5, 6: Search Metrics in Directions
      trackSearchSelection(item, slotType) {
          if (!item) return;

          // Metric 6: Search General
          AnalyticsTracker.track('search', 'search_general', {
              query: item.name || item.mitIdShort || item.mitId,
              context: 'directions',
              slot: slotType
          });

          // Category-Specific Search Metrics
          if (item.isNeighborhood) {
              // Metric 3: Search Group
              AnalyticsTracker.track('search', 'search_group', { group_id: item.id, name: item.name });
          } else if (item.spaceType === 'object') {
              // Metric 4: Search Object
              AnalyticsTracker.track('search', 'search_object', { object_id: item.mitId, type: item.useType });
          } else if (item.spaceType === 'space' || item.mitId) {
              // Metric 5: Search Space
              AnalyticsTracker.track('search', 'search_space', { space_id: item.mitId, level: item.level });
          }
      },
      updateUrl() {
          let query = Object.assign({}, this.$route.query);
          
          query.b = this.buildingMitId;

          if (this.navWizard.startObj && !this.isQrMode) {
              query.start = this.navWizard.startObj.mitId;
          } else if (!this.isQrMode) {
              delete query.start;
          }
          
          if (this.navWizard.destObj) {
              query.end = this.navWizard.destObj.mitId;
          } else {
              delete query.end;
          }
          
          if (this.navWizard.routingOption !== 'fastest') {
              query.opt = this.navWizard.routingOption;
          } else {
              delete query.opt;
          }
          
          this.$router.replace({ query }).catch(() => {});
      },
      customSearchFilter(item, queryText, itemText) {
        if (!item) return false;
        const searchText = (queryText || '').toLowerCase();
        if (!searchText) return true;

        const textMitIdShort = (item.mitIdShort || '').toLowerCase();
        const textUse = (item.useType || item.spaceType || '').toLowerCase();
        const textName = (item.name || '').toLowerCase();
        const textMitId = (item.mitId || '').toLowerCase();

        let piMatch = false;
        if (item.piList && Array.isArray(item.piList)) {
            piMatch = item.piList.some(pi => pi.toLowerCase().includes(searchText));
        }

        return textMitIdShort.includes(searchText) ||
               textUse.includes(searchText) ||
               textName.includes(searchText) ||
               textMitId.includes(searchText) ||
               piMatch;
    },
    // Update your existing getSpaceDisplayText to handle the array
    getSpaceDisplayText(item) {
        if (!item) return '';
        let name = item.name;
        let mitId = item.mitId;

        if (item.isNeighborhood) {
            let piText = (item.piList && item.piList.length) ? ' - PI ' + (Array.isArray(item.piList) ? item.piList.join(', ') : item.piList) : '';
            let groupSuffix = String(name).toLowerCase().includes('group') ? '' : ' Group';
            return `${name}${groupSuffix} (Main Space: ${mitId})${piText}`;
        } else if (item.spaceType === 'object') {
            let useType = item.useType || 'Object';
            
            if (item.name && item.name.length > 0) {
                return `${item.name} (${item.mitIdShort}) - ${useType}`;
            } else {
                return `${item.mitIdShort} - ${useType}`;
            }
        } else {
            // Spaces, rooms, etc.
            let useType = item.useType || item.spaceType || '';
            if (name) {
                return useType ? `${name} (${mitId}) ${useType}` : `${name} (${mitId})`;
            } else {
                return useType ? `${mitId} ${useType}` : `${mitId}`;
            }
        }
    },
      getObjectDefinitions(){
          return this.settingsService.getObjectDefinitions(true)
              .then((data) => { this.palletObjects = data.definitions; })
              .catch(() => { this.palletObjects = []; }); 
      },
      init(){
          this.loading = true;
          
          MitSpacesService.getGroups().then(data => {
              this.neighborhoods = data || [];
              if (this.mapService) {
                  this.mapService.setGroups(this.neighborhoods); 
              }
          }).finally(() => {
              Promise.all([
                  this.getSpacesList(),
                  this.getBuildingInfo(),
                  this.getBuildingData()
              ]).then(() => {
                  this.initMap();
                  
                  // Read standard route URL parameters
                  let query = this.$route.query || {};
                  
                  if (query.opt) {
                      this.navWizard.routingOption = query.opt;
                  }

                  if (query.start && !this.isQrMode) {
                      let sObj = this.startOptions.find(o => String(o.mitId) === String(query.start));
                      if (sObj) this.navWizard.startObj = sObj;
                  }
                  if (query.end) {
                      let eObj = this.destOptions.find(o => String(o.mitId) === String(query.end));
                      if (eObj) this.navWizard.destObj = eObj;
                  }

                  // Handle QR Startup
                    if(this.isQrMode && this.qrParamId) {
                        let foundStart = this.startOptions.find(opt => String(opt.mitId) === String(this.qrParamId));
                        
                        // NEW: Search the navigation network data for the QR code's properties
                        if (!foundStart && this.navFeaturesModel) {
                            let navNode = this.navFeaturesModel.find(f => f.properties && String(f.properties.mitId) === String(this.qrParamId));
                            if (navNode && navNode.properties) {
                                // Assign the properties (which includes the custom name) to our start object
                                foundStart = navNode.properties; 
                            }
                        }
                        // Track the scan using the name if it exists, fallback to ID
                        const metricName = (foundStart && foundStart.name) ? foundStart.name : this.qrParamId;
                        // Custom codes are tracked separately below, not as a scan of the signage
                        if (!this.fromCustomQr) {
                            AnalyticsTracker.track('qr_scans', metricName, {
                                building_id: this.buildingMitId,
                                action: 'directions_startup',
                                qr_id: this.qrParamId
                            });
                        }

                        if(foundStart) {
                            this.navWizard.startObj = foundStart;
                        } else {
                            this.navWizard.startObj = {
                                mitId: this.qrParamId,
                                mitIdShort: this.qrParamId,
                                name: 'Scanned Location',
                                mitBuildingId: this.buildingMitId,
                                isNavNode: true
                            };
                        }
                    }

                  if (this.fromCustomQr) {
                      AnalyticsTracker.track('custom_qr', 'scanned', this.customQrMetricData());
                  }

                  // Start navigation immediately if valid states are bound from URL
                  if (this.navWizard.startObj && this.navWizard.destObj) {
                      this.startNavigationMode();
                  }

              }).catch(error => {
                  console.error("Initialization Error:", error);
              }).finally(() => {
                  this.loading = false;
              });
          });
      },
      initMap(){
          this.floors.splice(0, this.floors.length);
          for(let x = 0; x < this.info.levels.length; x++){
             this.floors.push({name:'Floor '+this.info.levels[x], val:parseInt(this.info.levels[x])});
          }
          if (this.floors.length > 0) this.selectedFloor = this.floors[0].val;

          this.mapService.setEditMode(false);
          this.mapService.enableZoom(".zoom-in-btn", ".zoom-out-btn", ".zoom-reset-btn");
      },
      getSpacesList(){
          return MitSpacesService.getSpacesList(this.buildingMitId).then( data => { this.spacesList = data.spaces; });
      },
      getBuildingInfo(){
          return MitSpacesService.getBuildingStats(this.buildingMitId).then( data => {
              this.info.levels = data.stats.levels;
              this.info.floorDict = data.floors;
          });
      },
      getBuildingData() {
          return new Promise ((resolve, reject) => {
              let reqNav = MitSpacesService.getNavigation
                  ? MitSpacesService.getNavigation(this.buildingMitId).catch(() => ({ features: [] }))
                  : Promise.resolve({ features: [] });
                  
              // Conditionally fetch structural data on initial load
              let reqStruct = this.showStructures
                  ? MitSpacesService.getBuildingCoordinates(this.buildingMitId, {types:'window,floor'})
                  : Promise.resolve({});
                                   
              let requests = [
                  MitSpacesService.getBuildingGeoJson(this.buildingMitId, {types:'space,door,object'}),
                  reqStruct, 
                  Promise.resolve({}), 
                  reqNav
              ];
              
              Promise.all(requests).then((values) => {
                  let mapModelGeo = this.mapService.convertGeoJSONtoMapModel(values[0]);
                  let mapModelStruct = values[1];
                  
                  this.doorsModel = mapModelGeo.door;
                  this.spacesModel = mapModelGeo.space;
                  this.objectsModel = mapModelGeo.object;
                  
                  // If structures were loaded during this initial pass, map them and flag as loaded
                  if (this.showStructures && mapModelStruct && mapModelStruct.floor) {
                      this.windowsModel = mapModelStruct.window;
                      this.structuresModel = mapModelStruct.floor;
                      this.structuresLoaded = true;
                  }
                  
                  // Values[3] contains the navigation data
                  this.navFeaturesModel = values[3] && values[3].features ? values[3].features : [];
                  resolve();
              }).catch( error => { reject(error); });
          });
      },
        async toggleStructures() {
          this.showStructures = !this.showStructures;
          
          if (this.showStructures && !this.structuresLoaded) {
              this.loadingStructures = true;
              try {
                  let mapModelStruct = await MitSpacesService.getBuildingCoordinates(this.buildingMitId, {types:'window,floor'});
                  this.windowsModel = mapModelStruct.window;
                  this.structuresModel = mapModelStruct.floor;
                  this.structuresLoaded = true;
              } catch (err) {
                  console.error("Failed to load structures", err);
                  this.showStructures = false;
              } finally {
                  this.loadingStructures = false;
                  this.drawMap();
              }
          } else {
              this.drawMap();
          }
      },
      changeFloor(){ this.drawMap(); },
      drawMap() {
          if (!this.spacesModel || !this.spacesModel[this.selectedFloor]) return;
          
          // Switch map rendering mode based on the toggle state
          this.mapService.setMapDisplayOptions(this.showStructures ? 'Structures' : 'Default', false);
          
          let featuresToDraw = [];
          if (!this.navWizard.inProgress) {
              featuresToDraw = this.navFeaturesModel;
          } else if (this.currentInstruction) {
              featuresToDraw = this.currentInstruction.routeFeatures;
          }
          
          let spaceSelected = null;
          let selectedGroupId = null;
          if (this.navWizard.destObj) {
              if (this.navWizard.destObj.isNeighborhood) {
                  selectedGroupId = this.navWizard.destObj.id;
              }
              if (this.navWizard.inProgress && String(this.navWizard.destObj.level) === String(this.selectedFloor)) {
                  spaceSelected = this.navWizard.destObj.mitId;
              }
          }

          this.mapService.createMap(
              this.spacesModel, 
              this.objectsModel, 
              this.doorsModel, 
              this.windowsModel, 
              this.structuresModel, 
              this.info.floorDict, 
              this.size.w, 
              this.size.h, 
              this.selectedFloor, 
              null, 
              spaceSelected, 
              null, 
              featuresToDraw,
              selectedGroupId 
          );
          
          if (this.navWizard.inProgress && this.navWizard.route.length > 0) {
              this.renderNavigationStep();
          }
      },
      async startNavigationMode() {
          try {
              this.navWizard.errorMsg = '';
              this.loading = true;
              
              let startBldg = String(this.navWizard.startObj.mitBuildingId || this.navWizard.startObj.buildingMitId || this.buildingMitId);
              let destBldg = String(this.navWizard.destObj.mitBuildingId || this.navWizard.destObj.buildingMitId || this.buildingMitId);

              if (startBldg !== destBldg) {
                  this.navWizard.errorMsg = 'Sorry, these direction start and end are in different buildings. We only support directions within a single building.';
                  this.loading = false;
                  return; 
              }

              if (String(this.buildingMitId) !== startBldg) {
                  this.buildingMitId = startBldg;
                  await Promise.all([
                      this.getBuildingInfo(),
                      this.getBuildingData()
                  ]);
                  this.initMap();
              }

              let startId = this.navWizard.startObj.mitId;
              let destId = this.navWizard.destObj.mitId;

              // 3. USE CASE: Directions Started
              AnalyticsTracker.track('directions', 'start_directions', { 
                start_id: startId, 
                dest_id: destId,
                routing_option: this.navWizard.routingOption,
                started_via_qr: this.isQrMode // <-- Add this line
              });

              // Pass our routingOption logic parameter into the backend
              let routeData = await this.navEngine.getRoute(startId, destId, this.navWizard.routingOption);

              if (!routeData || !routeData.features || routeData.features.length === 0) {
                  throw new Error('No continuous path exists across the navigation network between these two locations.');
              }

              this.navWizard = Object.assign({}, this.navWizard, {
                  route: this.navEngine.generateFloorInstructions(routeData),
                  currentRouteIndex: 0,
                  inProgress: true
              });

              setTimeout(() => {
                  if (this.navWizard.route.length > 0) {
                      let targetFloor = parseInt(this.navWizard.route[0].floor);
                      if (this.selectedFloor !== targetFloor) {
                          this.selectedFloor = targetFloor;
                          this.changeFloor();
                      } else {
                          this.drawMap();
                      }
                  }
              }, 50);

          } catch (err) {
                console.error("[Vue UI] Navigation Execution Crashed:", err);
                this.navWizard.errorMsg = (err.response && err.response.data && err.response.data.error) 
                    || err.message 
                    || "Could not find a route.";
          } finally {
              this.loading = false;
          }
      },
      cancelNavigation() {
          // 1. Exit active routing UI
          this.navWizard.inProgress = false;
          
          // 2. Clear ALL bound objects and routing arrays
          this.navWizard.startObj = null;
          this.navWizard.destObj = null;
          this.navWizard.route = [];
          this.navWizard.currentRouteIndex = 0;
          this.navWizard.errorMsg = '';
          
          // 3. Clear the drawn lines on the SVG
          if (this.directionsDisplayService) {
              this.directionsDisplayService.clearRoute();
          }
          
          // 4. Disable QR mode entirely
          this.isQrMode = false;
          this.qrParamId = null;
          
          // 5. Scrub the URL so a browser refresh doesn't reload the old route
          let newQuery = Object.assign({}, this.$route.query);
          delete newQuery.start;
          delete newQuery.end;
          delete newQuery.qr;
          
          // Replace URL (catching benign duplicate navigation errors)
          this.$router.replace({ query: newQuery }).catch(err => {
              if (err.name !== 'NavigationDuplicated') throw err;
          });
      },
      renderNavigationStep() {
          try {
              this.directionsDisplayService.clearRoute();

              if (!this.currentInstruction) return;

              let floorFeatures = this.currentInstruction.routeFeatures;
              
              if (floorFeatures.length > 0) {
                  this.directionsDisplayService.drawRoute(floorFeatures);
              }

              let startCoord = null;
              if (floorFeatures.length > 0) {
                  let firstFeat = floorFeatures[0];
                  startCoord = firstFeat.geometry.type === 'LineString' 
                      ? firstFeat.geometry.coordinates[0] 
                      : firstFeat.geometry.coordinates;
              }
              
              let destCoord = null;
              if (floorFeatures.length > 0) {
                  let lastFeat = floorFeatures[floorFeatures.length - 1];
                  destCoord = lastFeat.geometry.type === 'LineString' 
                      ? lastFeat.geometry.coordinates[lastFeat.geometry.coordinates.length - 1] 
                      : lastFeat.geometry.coordinates;
              }

              let isLastStep = this.navWizard.currentRouteIndex === this.navWizard.route.length - 1;
              this.directionsDisplayService.drawMarkers(startCoord, destCoord, isLastStep);

              if (this.mapService && this.mapService.zoomToFeatures) {
                  this.mapService.zoomToFeatures(floorFeatures);
              }

          } catch (err) {
              console.error("[Vue UI] Error inside directionsDisplayService rendering:", err);
          }
      },
      nextNavStep() {
          if (this.navWizard.currentRouteIndex < this.navWizard.route.length - 1) {
              this.navWizard.currentRouteIndex++;
              this.selectedFloor = parseInt(this.currentInstruction.floor);
              // 4. USE CASE: Directions changed floor
              AnalyticsTracker.track('directions', 'change_floor', {
                    start_id: this.navWizard.startObj ? this.navWizard.startObj.mitId : null,
                    dest_id: this.navWizard.destObj ? this.navWizard.destObj.mitId : null
              });

              this.changeFloor();
          } else {
              // 5. USE CASE: Directions Finished (User clicked the final 'Finish Navigation' button)
              AnalyticsTracker.track('directions', 'finish_directions', {
                start_id: this.navWizard.startObj ? this.navWizard.startObj.mitId : null,
                dest_id: this.navWizard.destObj ? this.navWizard.destObj.mitId : null,
                started_via_qr: this.isQrMode // <-- Add this line
              });
              this.cancelNavigation();
          }
      },
      prevNavStep() {
          if (this.navWizard.currentRouteIndex > 0) {
              this.navWizard.currentRouteIndex--;
              this.selectedFloor = parseInt(this.currentInstruction.floor);
              this.changeFloor();
          }
      },
      _getSpaceFeature(spaceObj) {
          if (!spaceObj) return null;

          let lvl = spaceObj.level;
          let id = spaceObj.mitId;
          
          let f = null;

          if (this.spacesModel && this.spacesModel[lvl]) {
              f = this.spacesModel[lvl].features.find(feat => feat.properties.mitId === id);
          }
          if (!f && this.objectsModel && this.objectsModel[lvl]) {
              f = this.objectsModel[lvl].features.find(feat => feat.properties.mitId === id);
          }
          if (!f && this.doorsModel && this.doorsModel[lvl]) {
              f = this.doorsModel[lvl].features.find(feat => feat.properties.mitId === id);
          }
          
          if (!f && spaceObj.centroid) return spaceObj.centroid;
          
          return f; 
      }
  }
}
</script>

<style scoped>
  .white { background-color: #ffffff !important; }
  /* Directions QR button icon */
  .qr-download-icon { width: 24px; height: 24px; }
  .mit-directions-page { height: calc(100vh - 64px); position: relative; width: 100%; overflow: hidden; }
  .wizard-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 2; background-color: #f5f5f5; }

  .map-container-card { position: absolute; top: 0; left: 0; right: 0; bottom: 0; overflow: hidden; transition: opacity 0.2s ease; }
  .search-container { position: absolute; top: 10px; left: 10px; z-index: 3; }

  .scrollable { 
      position: absolute; top: 0; left: 0; right: 0; bottom: 0; 
      overflow: hidden; 
      touch-action: none !important; /* Critical for D3 mobile touch panning */
  }
  svg#mit-svg { 
      width: 100%; height: 100%; display: block; 
      touch-action: none !important; 
  }

  /* RIGHT TOOLBAR: Allows touches to pass through the invisible box bounds to the map */
  .toolbar-drawing-right { 
      position: absolute; 
      top: 80px; 
      right: 20px; 
      z-index: 3; 
      pointer-events: none; 
  }
  /* Catches touches ONLY on the actual visual panel/buttons */
  .toolbar-drawing-right .v-card, 
  .toolbar-drawing-right .v-btn { 
      pointer-events: auto; 
  }

  @media (max-width: 600px) {
      .toolbar-drawing-right {
          top: auto !important;
          bottom: 0px !important;
          left: 0px !important;
          right: 0px !important;
          width: 100% !important;
      }
      .toolbar-drawing-right .v-card {
          width: 100% !important;
          max-width: 100% !important;
          border-bottom-left-radius: 0 !important;
          border-bottom-right-radius: 0 !important;
          margin-bottom: 0 !important;
      }
  }

  .zoom-controls { 
      position: absolute; 
      bottom: 20px; 
      left: 20px; 
      z-index: 3; 
      display: flex; 
      flex-direction: column; 
      pointer-events: none;
  }
  .zoom-controls .v-btn {
      pointer-events: auto;
  }

  @media (max-width: 600px) {
      .zoom-controls {
          display: none !important;
      }
  }
</style>